import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Avatars from './pages/Avatars'
import AvatarDetails from './pages/AvatarDetails'
import Dialers from './pages/Dialers'
import DialerDetails from './pages/DialerDetails'
import Clients from './pages/Clients'
import ClientDetails from './pages/ClientDetails'
import Resellers from './pages/Resellers'
import Onboarding from './pages/Onboarding'
import Licenses from './pages/Licenses'
import Users from './pages/Users'
import VoiceSets from './pages/VoiceSets'
import KeywordCategories from './pages/KeywordCategories'
import AmFingerprints from './pages/AmFingerprints'
import AmVocab from './pages/AmVocab'
// KeywordSuggestions is now a tab in KeywordCategories
import Links from './pages/Links'
import Monitoring from './pages/MonitoringV2'
import MonitoringV3 from './pages/MonitoringV3'
import Servers from './pages/ServersV2'
import Templates from './pages/Templates'
import Containers from './pages/Containers'
import BackupStats from './pages/BackupStats'
import Financials from './pages/Financials'
import RequestInbox from './pages/RequestInbox'
import Issues from './pages/Issues'
import FieldTeamRoutes from './pages/FieldTeam'
import AsteriskBot from './pages/AsteriskBot'
import CampaignBuilder from './pages/CampaignBuilder'


// Old /stats links (?tab=calls&search=…) → Monitoring's call logs for all clients.
function StatsRedirect() {
  const [params] = useSearchParams()
  const next = new URLSearchParams({ tab: params.get('tab') === 'calls' ? 'call_logs' : 'stats' })
  if (params.get('search')) next.set('search', params.get('search'))
  return <Navigate to={`/monitoring-v3?${next}`} replace />
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" style={{ minHeight: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

function AdminRoute({ children }) {
  const { isAdmin, isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" style={{ minHeight: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  return children
}

function App() {
  const { isAuthenticated } = useAuth()

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <Login />
      } />

      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        {/* Statistics moved into Monitoring (per-client Stats tab + "All clients") */}
        <Route path="stats" element={<StatsRedirect />} />
        <Route path="monitoring" element={<Monitoring />} />
        <Route path="monitoring-v3" element={<MonitoringV3 />} />
        <Route path="servers" element={<Servers />} />
        <Route path="servers/:id" element={<Servers />} />
        <Route path="avatars" element={<Avatars />} />
        <Route path="avatars/:id" element={<AvatarDetails />} />
        <Route path="dialers" element={<Dialers />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetails />} />
        <Route path="resellers" element={<Resellers />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="templates" element={<Templates />} />
        <Route path="containers" element={<Containers />} />
        {/* old link — the Linux machines list is now a tab of Containers */}
        <Route path="machines" element={<Navigate to="/containers?tab=linux" replace />} />
        <Route path="dialer-details/:id" element={<DialerDetails />} />
        <Route path="licenses" element={<Licenses />} />
        <Route path="users" element={
          <AdminRoute>
            <Users />
          </AdminRoute>
        } />
        <Route path="voice-sets" element={<VoiceSets />} />
        <Route path="campaigns" element={<CampaignBuilder />} />
        <Route path="keyword-categories" element={<KeywordCategories />} />
        <Route path="am-fingerprints" element={
          <AdminRoute>
            <AmFingerprints />
          </AdminRoute>
        } />
        <Route path="am-vocab" element={
          <AdminRoute>
            <AmVocab />
          </AdminRoute>
        } />
        <Route path="links" element={<Links />} />
        <Route path="backup-stats" element={<BackupStats />} />
        <Route path="financials" element={<Financials />} />
        <Route path="request-inbox" element={<RequestInbox />} />
        <Route path="issues" element={<Issues />} />
        <Route path="field-team/*" element={<FieldTeamRoutes />} />
        <Route path="asterisk-bot" element={
          <AdminRoute>
            <AsteriskBot />
          </AdminRoute>
        } />
        {/* old AudioSocket Bots page — its tabs now live on Templates, Servers and Containers */}
        <Route path="audiosocket-bots" element={<Navigate to="/templates" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App